// LoginForm — verbatim DOM parity with legacy stbarts (index.html L1664-1676).
// `.login-overlay > .login-box > h2 + p + .login-form (email + password +
// turnstileWidget + login-error + button)`. Turnstile widget renders via a
// reactive effect that fires as soon as both the container ref AND the site
// key signal are ready, fixing the alpha.10.1 race condition where the
// one-shot onMount bailed silently if config hadn't resolved yet.

import { createEffect, createSignal, onCleanup, onMount } from 'solid-js';
import { actions, turnstileEnabledSignal, turnstileSiteKeySignal } from '../state/store';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
          theme?: string;
        },
      ) => string;
      reset: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

export function LoginForm() {
  const [email, setEmail] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [turnstileToken, setTurnstileToken] = createSignal<string | null>(null);
  const [submitting, setSubmitting] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [widgetRef, setWidgetRef] = createSignal<HTMLDivElement | undefined>(undefined);
  let widgetId: string | null = null;
  let script: HTMLScriptElement | null = null;

  function tryRender() {
    const key = turnstileSiteKeySignal();
    const ref = widgetRef();
    if (!key || !ref || !window.turnstile || widgetId !== null) return;
    widgetId = window.turnstile.render(ref, {
      sitekey: key,
      callback: (token) => setTurnstileToken(token),
      'error-callback': () => setTurnstileToken(null),
      'expired-callback': () => setTurnstileToken(null),
    });
  }

  createEffect(() => {
    if (!turnstileEnabledSignal()) return;
    tryRender();
  });

  onMount(() => {
    if (!turnstileEnabledSignal()) return;
    window.onTurnstileLoad = tryRender;
    if (window.turnstile) {
      tryRender();
    } else if (!document.querySelector(`script[src^="${TURNSTILE_SRC}"]`)) {
      script = document.createElement('script');
      script.src = `${TURNSTILE_SRC}?onload=onTurnstileLoad`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  onCleanup(() => {
    delete window.onTurnstileLoad;
  });

  async function submit(e: Event) {
    e.preventDefault();
    setError(null);
    const token = turnstileToken();
    if (turnstileEnabledSignal() && !token) {
      setError('Vérification de sécurité en cours, réessayez dans un instant');
      return;
    }
    setSubmitting(true);
    try {
      await actions.login(email().trim(), password(), token);
    } catch (err) {
      const fallback =
        err instanceof Error && err.message
          ? err.message
          : String(err) || 'Échec de la connexion';
      console.error('[LoginForm] submit failed', err);
      setError(fallback);
      if (widgetId && window.turnstile) window.turnstile.reset(widgetId);
      setTurnstileToken(null);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div class="login-overlay">
      <div class="login-box">
        <h2>SB Screenshots</h2>
        <p>Connectez-vous pour accéder au viewer</p>
        <form class="login-form" onSubmit={submit}>
          <input
            type="email"
            placeholder="Email"
            required
            autocomplete="email"
            value={email()}
            onInput={(e) => setEmail(e.currentTarget.value)}
          />
          <input
            type="password"
            placeholder="Mot de passe"
            required
            autocomplete="current-password"
            value={password()}
            onInput={(e) => setPassword(e.currentTarget.value)}
          />
          {/* Container always present (matches legacy DOM L1671). When
              turnstile is disabled this is an empty div that still occupies
              the form's gap, preserving the vertical rhythm. */}
          <div ref={setWidgetRef} />
          <div class="login-error">{error() ?? ''}</div>
          <button
            type="submit"
            disabled={submitting() || (turnstileEnabledSignal() && !turnstileToken())}
          >
            {submitting() ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
}
